import React from "react";
import { Link, useLocation, useMatch } from "react-router-dom";
import styled from "styled-components";
import { ChevronLeft } from "react-feather";
import { useConnectorRegistryQuery } from "../../hooks/useConnectorApi";
import { spacing } from "../../design-system/tokens/spacing";
import { typographyMixin } from "../../design-system/tokens/typography";

const PageHeaderRow = styled.div`
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.tertiary};
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[3]} ${spacing[4]};

  a {
    color: ${({ theme }) => theme.colors.text.interactive};
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: ${spacing[1]};
  }

  a:hover {
    text-decoration: underline;
  }

  > span[aria-current] {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

/**
 * Breadcrumb shown on `/data-connectors/:id` and `/data-connectors/:id/tables/...`.
 * Lives inside the Shell's `breadcrumb` slot for connector routes.
 */
export const ConnectorPageBreadcrumb: React.FC = () => {
  const location = useLocation();
  const datasetMatch = useMatch(
    "/data-connectors/:connectionId/tables/:schema/:tableName",
  );
  const connectionMatch = useMatch({
    path: "/data-connectors/:connectionId",
    end: true,
  });
  const { data: connectorRows = [] } = useConnectorRegistryQuery();

  if (/^\/data-connectors\/[^/]+\/data-studio(?:\/|$)/.test(location.pathname)) {
    return null;
  }

  const connectionId =
    datasetMatch?.params.connectionId ?? connectionMatch?.params.connectionId;

  const registryRow = connectionId
    ? connectorRows.find(
        (row) =>
          row.connectionId === connectionId ||
          row.instances?.some((i) => i.connectorId === connectionId),
      )
    : undefined;
  const displayName = registryRow?.displayName ?? "Connection";

  const tableLabel = datasetMatch?.params.tableName
    ? decodeURIComponent(datasetMatch.params.tableName)
    : null;

  return (
    <PageHeaderRow aria-label="Breadcrumb">
      <Link to="/data-connectors">
        <ChevronLeft size={16} aria-hidden color="gray" />
        Data connectors
      </Link>
      <span aria-hidden>/</span>
      {connectionId ? (
        <Link to={`/data-connectors/${connectionId}`}>{displayName}</Link>
      ) : (
        <span>{displayName}</span>
      )}
      <span aria-hidden>/</span>
      {tableLabel ? (
        <span aria-current="page">{tableLabel}</span>
      ) : (
        <span aria-current="page">Connection detail</span>
      )}
    </PageHeaderRow>
  );
};

export default ConnectorPageBreadcrumb;
